import React, { Fragment, useEffect, useState } from 'react'
import MultipleStepsForm from 'components/funnel/MultipleStepsForm'
import InformationForm from 'components/bookings/InformationForm'
import MessageForm from 'components/bookings/MessageForm'
import GiftCardOrderSuccess from 'components/bookings/GiftCardOrderSuccess'
import GiftCardPaymentForm from 'components/bookings/GiftCardPaymentForm'
import display from 'lib/Display'
import BookingFaqs from 'components/bookings/BookingFaqs'
import lockIcon from 'images/icons/lock.svg'
import { isOrderPaid, isPaymentError } from 'lib/Checkout'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'

const stripePromise = loadStripe(process.env.STRIPE_PUBLISHABLE_KEY, {
  betas: ['blocked_card_brands_beta_2']
})

const FastCheckout = ({
  order: initialOrder,
  user: initialUser,
  authenticityToken,
  urls,
  images,
  steps,
  babaPhoneNumber
}) => {
  const [order, setOrder] = useState(initialOrder)
  const [user, setUser] = useState(initialUser)
  const [clientSecret, setClientSecret] = useState('')
  const [showPaymentForm, setShowPaymentForm] = useState(!isOrderPaid(order))

  useEffect(() => {
    fetch(urls.findOrCreatePaymentIntentURL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        payable_id: order.id,
        payable_type: 'GiftCardOrder',
        authenticity_token: authenticityToken
      })
    })
      .then(async (response) => {
        if (response.status === 404) {
          const errorData = await response.json().catch(() => ({}))
          console.error('GiftCardOrder not found:', errorData)
          return null
        }
        if (!response.ok) {
          throw new Error(`${response.status}`)
        }
        return response.json()
      })
      .then((data) => {
        if (data.payment_intent) {
          setClientSecret(data.payment_intent.client_secret)
        }
      })
      .catch((error) => {
        console.error('Network or server error:', error)
      })
  }, [clientSecret, order])

  useEffect(() => {
    const script = document.createElement('script')
    script.src =
      'https://widgets.rr.skeepers.io/generated/e0619e69-b4b3-dd44-911b-45b8a77e037e/e36c503a-fa16-45d8-b180-be41115fd0e4.js'
    script.async = true
    document.body.appendChild(script)
    return () => {
      document.body.removeChild(script)
    }
  }, [])

  useEffect(() => {
    setShowPaymentForm(!isOrderPaid(order))
  }, [order])

  const handleOrderModification = (data) => {
    setOrder(data.order)
  }

  const stepsArray = []

  const messageForm = (
    <MessageForm
      authenticityToken={authenticityToken}
      priceAfter={order.amount}
      personalizedMessage={order.personalizedMessage}
      updatePersonalizedMessageURL={urls.updatePersonalizedMessageURL}
      onOrderModification={handleOrderModification}
      giftCardsURL={urls.giftCardsURL}
    />
  )
  stepsArray.push({
    name: 'Personnalisation',
    content: messageForm,
    url: steps.messageURL
  })

  const informationForm = (
    <InformationForm
      authenticityToken={authenticityToken}
      user={user}
      onUserModification={setUser}
      urls={urls}
      priceAfter={order.amount}
    />
  )
  stepsArray.push({
    name: 'Informations',
    content: informationForm,
    url: steps.informationURL
  })

  if (showPaymentForm) {
    const appearance = {
      theme: 'stripe',
      variables: {
        colorPrimary: '#123B82',
        colorText: '#333333',
        colorDanger: '#E02D3C',
        fontFamily: 'Ideal Sans, system-ui, sans-serif'
      }
    }
    const loader = 'auto'
    const paymentForm = clientSecret ? (
      <Elements
        options={{
          clientSecret,
          appearance,
          loader,
          disallowedCardBrands: ['american_express']
        }}
        stripe={stripePromise}>
        <GiftCardPaymentForm
          user={user}
          leftToPay={order.leftToPay}
          paymentErrorMessage={order.paymentErrorMessage}
          urls={urls}
          order={order}
          authenticityToken={authenticityToken}
        />
      </Elements>
    ) : (
      <Fragment />
    )
    stepsArray.push({
      name: 'Paiement',
      content: paymentForm,
      url: steps.paymentURL
    })
  } else {
    const successNode = <GiftCardOrderSuccess urls={urls} />
    stepsArray.push({
      name: 'Paiement',
      content: successNode,
      url: steps.paymentURL
    })
  }

  let initialStep = 0
  if (isOrderPaid(order) || isPaymentError(order)) {
    initialStep = 2
  }

  return (
    <div className="container mb-3">
      <MultipleStepsForm
        images={images}
        homepageURL={urls.homepageURL}
        steps={stepsArray}
        initialStep={initialStep}>
        <Fragment>
          <div className="order-recap-container hidden-xs">
            <div className="gift-card-example">
              <img
                src={images.giftCardImage}
                className="img-responsive img-center"
                alt="Carte cadeau"
              />
            </div>
            <div className="content">
              <div className="totals-component">
                <div className="totals">
                  <div className="total-final row">
                    <div className="col-xs-8 total-wording">Total :</div>
                    <div className="col-xs-4 text-right total-amount">
                      {display.currency(order.amount)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row secure-payment-banner text-center">
            <div className="col-xs-12">
              <img
                src={lockIcon}
                alt="Paiement sécurisé"
                className="mr-2"
                height="12px"
                width="12px"
              />
              <strong>Paiement 100% sécurisé</strong>
            </div>
          </div>

          <div className="col-xs-12 text-center">
            <div
              className="center-child-link"
              id="e36c503a-fa16-45d8-b180-be41115fd0e4"></div>
          </div>
        </Fragment>
      </MultipleStepsForm>

      <BookingFaqs type="gift_card" babaPhoneNumber={babaPhoneNumber} />
    </div>
  )
}

export default FastCheckout
